import type { TableKitOptions } from '@tiptap/extension-table';
import type { StarterKitOptions } from '@tiptap/starter-kit';
import purify from 'dompurify';
import isNil from 'lodash/isNil';

import { cn } from '@ui/lib/utils';

export const PARAGRAPH_NODE_NAME = 'paragraph';

export const HEADING_NODE_NAME = 'heading';

export const TEXT_BLOCK_TYPES: string[] = [HEADING_NODE_NAME, PARAGRAPH_NODE_NAME];

// The extension options and RICH_TEXT_ALLOWED_TAGS below move together: an extension whose element
// is missing from the allowlist is silently lost on the reader's side. horizontalRule and underline
// are disabled on purpose. `style` (table widths) and `class` (code block language) are dropped
// knowingly: both are presentational, and an allowlisted `class` could paint any Tailwind utility.

export const RICH_TEXT_STARTER_KIT_OPTIONS: Partial<StarterKitOptions> = {
  heading: { levels: [1, 2, 3] },
  link: { openOnClick: false },
  horizontalRule: false,
  underline: false,
};

// Resizing would add drag handles to a body that is read on a phone and never sized by hand.
export const RICH_TEXT_TABLE_KIT_OPTIONS: Partial<TableKitOptions> = {
  table: { resizable: false },
};

// TextAlign's own `style` output is stripped by the sanitiser, so alignment is stored in this
// attribute instead (see richTextExtensions.ts); only these three literal values are styled.
export type RichTextAlignment = 'left' | 'center' | 'right';

export const RICH_TEXT_ALIGNMENTS: RichTextAlignment[] = ['left', 'center', 'right'];

export const RICH_TEXT_ALIGN_ATTRIBUTE = 'data-text-align';

export const parseRichTextAlignment = (value: string | null): RichTextAlignment | null =>
  RICH_TEXT_ALIGNMENTS.find((alignment) => alignment === value) ?? null;

// The XSS gate: bodies are admin-authored HTML, so anything outside this allowlist is dropped.
export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'h1',
  'h2',
  'h3',
  'strong',
  'em',
  's',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'br',
  'table',
  'colgroup',
  'col',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

export const RICH_TEXT_ALLOWED_ATTRIBUTES = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'title',
  'start',
  'type',
  'colspan',
  'rowspan',
  'colwidth',
  RICH_TEXT_ALIGN_ATTRIBUTE,
];

export const sanitizeRichTextHtml = (html: string): string => {
  // Without a DOM, purify.sanitize returns its input untouched. Returning nothing instead
  // keeps a server-rendered page from shipping unsanitised HTML.
  if (!purify.isSupported) {
    return '';
  }
  return purify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTRIBUTES,
    // Off, so the list above is the whole list. DOMPurify lets every data-* attribute through by
    // default, whatever the allowlist says, and the one this body may carry is named in it.
    ALLOW_DATA_ATTR: false,
  });
};

// Shared by the editor and the read-only renderer so both look the same. Element styles are
// explicit because Tailwind's preflight is scoped, not global (see preflight-scoped.css).
export const RICH_TEXT_PROSE_CLASS_NAME = cn(
  'text-left text-base text-foreground',
  '[&>*]:mt-3 [&>*]:mb-0 [&>*:first-child]:mt-0',
  '[&>h1]:mt-5 [&>h2]:mt-5 [&>h3]:mt-4',
  '[&_h1]:text-3xl [&_h1]:leading-tight [&_h1]:font-bold',
  '[&_h2]:text-2xl [&_h2]:leading-tight [&_h2]:font-bold',
  '[&_h3]:text-lg [&_h3]:leading-snug [&_h3]:font-semibold',
  // font-normal is explicit: this also renders inside legacy MUI pages whose base font-weight
  // would otherwise make the whole body look bold.
  '[&_p]:text-base [&_p]:leading-relaxed [&_p]:font-normal',
  '[&>ul]:list-disc [&>ul]:pl-5 [&>ol]:list-decimal [&>ol]:pl-5',
  '[&_li]:my-1.5 [&_li]:text-base [&_li]:leading-relaxed [&_li]:font-normal [&_li>p]:m-0',
  '[&_strong]:font-semibold [&_em]:italic [&_s]:line-through',
  '[&_a]:text-primary [&_a]:underline',
  '[&_img]:block [&_img]:h-auto [&_img]:w-full [&_img]:rounded-lg',
  '[&>blockquote]:border-l-2 [&>blockquote]:border-border [&>blockquote]:pl-4',
  '[&_blockquote]:text-muted-foreground',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.875em]',
  '[&>pre]:overflow-x-auto [&>pre]:rounded-lg [&>pre]:bg-muted [&>pre]:p-3 [&>pre]:font-mono [&>pre]:text-sm',
  '[&>pre_code]:bg-transparent [&>pre_code]:p-0',
  // display:block is what lets a table scroll inside itself instead of stretching the page on a
  // phone or in the notification panel. The overflow stays on the table, never on the body.
  '[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
  '[&_th]:text-base [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:text-base [&_td]:font-normal',
  '[&_th>p]:m-0 [&_td>p]:m-0',
  '[&_[data-text-align=left]]:text-left',
  '[&_[data-text-align=center]]:text-center',
  '[&_[data-text-align=right]]:text-right'
);

export interface EditableAttributesOptions {
  className: string;
  multiline: boolean;
  invalid: boolean;
  readOnly: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

export const buildEditableAttributes = ({
  className,
  multiline,
  invalid,
  readOnly,
  ariaLabel,
  ariaLabelledBy,
}: EditableAttributesOptions): Record<string, string> => ({
  class: className,
  role: 'textbox',
  'aria-multiline': String(multiline),
  ...(isNil(ariaLabel) ? {} : { 'aria-label': ariaLabel }),
  ...(isNil(ariaLabelledBy) ? {} : { 'aria-labelledby': ariaLabelledBy }),
  ...(invalid ? { 'aria-invalid': 'true' } : {}),
  ...(readOnly ? { 'aria-readonly': 'true' } : {}),
});
